import {
  DND_DRAGGABLE_PROPS,
  Icon,
  Menu,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  Text,
} from "@salestrak/ui";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React from "react";

import { EntityTag } from "#app/components/entity";
import type { WorkspaceWorkflow } from "#app/lib/data";
import type { Omittable } from "@salestrak/ui";

// The sidebar row renders a workflow exactly as `useAppData()` holds it —
// `WorkspaceWorkflow` already carries `visibility`, `viewerAccessLevel` and
// `viewerCanManageSharing`, with the schema's own nullability (R2, R4).

/**
 * A workflow row in a sidebar menu. `sortable` makes the whole row a dnd-kit
 * drag source (there is no separate grip: the row itself is the handle, and
 * the grab cursor is its affordance).
 */
export function WorkflowItem({
  workflow,
  isActive,
  onClick,
  onEdit,
  onShare,
  sortable = false,
}: Omittable<{
  workflow: WorkspaceWorkflow;
  isActive: boolean;
  onClick: () => void;
  onEdit?: () => void;
  onShare?: () => void;
  sortable?: boolean;
}>) {
  const {
    // dnd-kit sets role="button" on sortable items; strip it — the row is a
    // list item whose button child owns the role.
    attributes: { role: _role, ...attributes },
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ disabled: !sortable, id: workflow.workflowId });

  return (
    <SidebarMenuItem
      ref={setNodeRef}
      style={{
        cursor: sortable ? "grab" : undefined,
        opacity: isDragging ? 0.5 : undefined,
        // Vertical-only reorder: the x delta is dropped so rows never drift
        // out of the sidebar column.
        transform: sortable
          ? CSS.Transform.toString(transform ? { ...transform, x: 0 } : null)
          : undefined,
        transition: sortable ? transition : undefined,
      }}
      {...(sortable ? DND_DRAGGABLE_PROPS : undefined)}
      {...attributes}
      {...listeners}
    >
      <SidebarMenuButton
        disabled={isDragging}
        isActive={isActive}
        onClick={onClick}
      >
        <EntityTag
          entity={{ id: workflow.workflowId, kind: "workflow", workflow }}
          interactive={false}
          weight="regular"
        />
        {workflow.visibility === "private" && (
          <Icon color="muted" name="lock" size="sm" />
        )}
      </SidebarMenuButton>
      {(onEdit || onShare) && (
        <Menu>
          <MenuTrigger
            render={<SidebarMenuAction aria-label="Workflow actions" showOnHover />}
          >
            <Icon color="muted" name="more-horizontal" size="sm" />
          </MenuTrigger>
          <MenuPopup align="start" side="right">
            {onEdit ? (
              <MenuItem onClick={onEdit}>
                <Icon name="settings" size="sm" />
                <Text>Edit workflow</Text>
              </MenuItem>
            ) : null}
            {onEdit && onShare ? <MenuSeparator /> : null}
            {onShare ? (
              <MenuItem onClick={onShare}>
                <Icon name="users" size="sm" />
                <Text>Sharing and permissions</Text>
              </MenuItem>
            ) : null}
          </MenuPopup>
        </Menu>
      )}
    </SidebarMenuItem>
  );
}
